import {
  useQuery,
  useMutation,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { DisciplinesService } from "@/api/disciplines";
import { DisciplinesRepository } from "@/repositories/disciplines";
import type {
  Discipline,
  DisciplineRequestData,
  DisciplinesResponse,
} from "@/api/disciplines/types";
import { useGymDisciplines } from "@/queries/use-gyms";
import { queryKeys } from "./constants";

interface UseDisciplinesOptions {
  enabled?: boolean;
}

interface UseDisciplineOptions {
  enabled?: boolean;
}

export const useDisciplines = (options: UseDisciplinesOptions = {}) => {
  const { enabled = true } = options;

  return useQuery<DisciplinesResponse, Error>({
    queryKey: queryKeys.disciplines.all,
    queryFn: async () => DisciplinesService.instance.getDisciplines(),
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

interface UseDisciplinesByGymOptions {
  enabled?: boolean;
}

export const useDisciplinesByGym = (
  gymId: number | undefined,
  options: UseDisciplinesByGymOptions = {},
) => {
  return useQuery<DisciplinesResponse, Error>({
    queryKey: queryKeys.disciplines.byGymId(gymId),
    queryFn: () => DisciplinesService.instance.getDisciplineByGymId(gymId!),
    enabled: (options.enabled ?? true) && gymId !== undefined,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

interface UseScopedDisciplinesOptions {
  isGymScoped: boolean;
}

export interface DisciplineOption {
  id: string;
  name: string;
}

export const useScopedDisciplines = (
  gymId: number | undefined,
  { isGymScoped }: UseScopedDisciplinesOptions,
) => {
  const { data: chainData, isLoading: isChainLoading } = useDisciplines({
    enabled: !isGymScoped,
  });
  const { data: gymData, isLoading: isGymLoading } = useGymDisciplines(
    gymId,
    "active",
    { enabled: isGymScoped },
  );

  const data = useMemo<DisciplineOption[] | undefined>(() => {
    const source = isGymScoped
      ? gymData?.gymDisciplines
      : chainData?.disciplines;
    return source?.map(({ id, name }) => ({ id: String(id), name }));
  }, [isGymScoped, gymData, chainData]);

  return {
    data,
    isLoading: isGymScoped ? isGymLoading : isChainLoading,
  };
};

export const disciplineQueryOptions = (id: string) =>
  queryOptions({
    queryKey: queryKeys.disciplines.detail(id),
    queryFn: () => DisciplinesService.instance.getDiscipline(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useDiscipline = (
  id: string,
  options: UseDisciplineOptions = {},
) => {
  const { enabled = true } = options;

  return useQuery({
    ...disciplineQueryOptions(id),
    enabled: enabled && !!id,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useCreateDiscipline = () => {
  const queryClient = useQueryClient();

  return useMutation<Discipline, Error, DisciplineRequestData>({
    mutationFn: (data) => DisciplinesRepository.instance.createDiscipline(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.disciplines.all });
    },
  });
};

export const useUpdateDiscipline = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Discipline,
    Error,
    { id: string; data: DisciplineRequestData }
  >({
    mutationFn: ({ id, data }) =>
      DisciplinesRepository.instance.updateDiscipline(id, data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.disciplines.detail(variables.id),
      });
    },
    onSuccess: (updatedDiscipline, variables) => {
      queryClient.setQueryData(
        queryKeys.disciplines.detail(variables.id),
        updatedDiscipline,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.disciplines.all });
    },
  });
};

export const useDeleteDisciplines = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string[]>({
    mutationFn: (ids) => DisciplinesService.instance.deleteDisciplines(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.disciplines.all });
    },
  });
};
